/**
 * 반복 규칙. RFC 5545 RRULE 중 빠른 입력이 만드는 부분만 다룬다.
 *   FREQ=DAILY|WEEKLY|MONTHLY|YEARLY, INTERVAL, BYDAY(MO / 1MO / -1FR), BYMONTHDAY(1~31, -1), BYMONTH
 * 날짜 단위로만 계산한다 (시각은 할일의 마감 시각을 그대로 쓴다).
 */
import { addDays, type DateStr, dateParts, daysBetween, daysInMonth, mondayOf, monthsBetween, WEEKDAY_NAMES, weekday } from "./dates";

export type Freq = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";

export interface Recurrence {
  freq: Freq;
  interval: number;
  /** 요일 (0=월). 매달 규칙이면 몇째 주인지(nth: 1~4, -1=마지막)와 함께 */
  byDay?: { day: number; nth?: number }[];
  /** 매달 며칠 (-1 = 말일) */
  byMonthDay?: number;
  byMonth?: number;
}

const DAY_CODES = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;

export function formatRRule(r: Recurrence): string {
  const parts = [`FREQ=${r.freq}`];
  if (r.interval > 1) parts.push(`INTERVAL=${r.interval}`);
  if (r.byDay?.length) parts.push(`BYDAY=${r.byDay.map((d) => `${d.nth ?? ""}${DAY_CODES[d.day]}`).join(",")}`);
  if (r.byMonthDay !== undefined) parts.push(`BYMONTHDAY=${r.byMonthDay}`);
  if (r.byMonth !== undefined) parts.push(`BYMONTH=${r.byMonth}`);
  return parts.join(";");
}

export function parseRRule(s: string): Recurrence | null {
  const fields = new Map(
    s
      .replace(/^RRULE:/, "")
      .split(";")
      .map((p) => p.split("=") as [string, string]),
  );
  const freq = fields.get("FREQ");
  if (freq !== "DAILY" && freq !== "WEEKLY" && freq !== "MONTHLY" && freq !== "YEARLY") return null;
  const interval = Number(fields.get("INTERVAL") ?? 1);
  if (!Number.isInteger(interval) || interval < 1 || interval > 365) return null;
  const r: Recurrence = { freq, interval };
  const byDay = fields.get("BYDAY");
  if (byDay) {
    const days = byDay.split(",").map((code) => {
      const m = /^(-1|[1-4])?(MO|TU|WE|TH|FR|SA|SU)$/.exec(code);
      return m ? { day: DAY_CODES.indexOf(m[2] as (typeof DAY_CODES)[number]), ...(m[1] ? { nth: Number(m[1]) } : {}) } : null;
    });
    if (days.some((d) => d === null)) return null;
    r.byDay = days as { day: number; nth?: number }[];
  }
  const md = fields.get("BYMONTHDAY");
  if (md !== undefined) {
    const n = Number(md);
    if (!Number.isInteger(n) || n === 0 || n < -1 || n > 31) return null;
    r.byMonthDay = n;
  }
  const bm = fields.get("BYMONTH");
  if (bm !== undefined) {
    const n = Number(bm);
    if (!Number.isInteger(n) || n < 1 || n > 12) return null;
    r.byMonth = n;
  }
  return r;
}

export function isValidRRule(s: string): boolean {
  return parseRRule(s) !== null;
}

/** 그 달의 n번째(-1=마지막) 요일인지 */
function isNthWeekday(date: DateStr, day: number, nth: number): boolean {
  if (weekday(date) !== day) return false;
  const { year, month, day: d } = dateParts(date);
  if (nth === -1) return d + 7 > daysInMonth(year, month);
  return Math.ceil(d / 7) === nth;
}

/** 매달 n일. 그 달에 없는 날(31일 등)은 말일로 */
function isMonthDay(date: DateStr, n: number): boolean {
  const { year, month, day } = dateParts(date);
  const last = daysInMonth(year, month);
  return n === -1 ? day === last : day === Math.min(n, last);
}

/** date가 anchor에서 시작한 반복의 한 회차인지 */
function matches(r: Recurrence, date: DateStr, anchor: DateStr): boolean {
  switch (r.freq) {
    case "DAILY":
      return daysBetween(anchor, date) % r.interval === 0;
    case "WEEKLY": {
      const weeks = daysBetween(mondayOf(anchor), mondayOf(date)) / 7;
      if (weeks % r.interval !== 0) return false;
      const days = r.byDay?.length ? r.byDay.map((d) => d.day) : [weekday(anchor)];
      return days.includes(weekday(date));
    }
    case "MONTHLY": {
      if (monthsBetween(anchor, date) % r.interval !== 0) return false;
      if (r.byDay?.length) return r.byDay.some((d) => isNthWeekday(date, d.day, d.nth ?? 1));
      return isMonthDay(date, r.byMonthDay ?? dateParts(anchor).day);
    }
    case "YEARLY": {
      const a = dateParts(anchor);
      const p = dateParts(date);
      if ((p.year - a.year) % r.interval !== 0) return false;
      if (p.month !== (r.byMonth ?? a.month)) return false;
      return isMonthDay(date, r.byMonthDay ?? a.day);
    }
  }
}

/** 8년 안에서 찾는다 (매년 2월 29일도 찾을 수 있게) */
const SEARCH_DAYS = 366 * 8;

/** from 이상인 첫 회차 */
export function firstOccurrence(r: Recurrence, from: DateStr, anchor: DateStr = from): DateStr | null {
  for (let i = 0; i <= SEARCH_DAYS; i++) {
    const d = addDays(from, i);
    if (matches(r, d, anchor)) return d;
  }
  return null;
}

/** after 다음 회차 */
export function nextOccurrence(r: Recurrence, after: DateStr, anchor: DateStr = after): DateStr | null {
  return firstOccurrence(r, addDays(after, 1), anchor);
}

const NTH_NAMES: Record<number, string> = { 1: "첫째", 2: "둘째", 3: "셋째", 4: "넷째", [-1]: "마지막" };
const UNIT_NAMES: Record<Freq, string> = { DAILY: "일", WEEKLY: "주", MONTHLY: "달", YEARLY: "년" };

/** 사람이 읽는 설명: "매주 월·수·금", "격주 금요일", "매달 마지막 날", "3일마다 (완료 후)" */
export function describeRecurrence(r: Recurrence, fromCompletion = false): string {
  const tail = fromCompletion ? " (완료 후)" : "";
  const dayList = (days: { day: number }[]) => (days.length === 1 ? `${WEEKDAY_NAMES[days[0]!.day]}요일` : days.map((d) => WEEKDAY_NAMES[d.day]).join("·"));
  if (r.freq === "DAILY") return (r.interval === 1 ? "매일" : `${r.interval}일마다`) + tail;
  if (r.freq === "WEEKLY") {
    const days = r.byDay ?? [];
    const set = days.map((d) => d.day).sort().join("");
    if (r.interval === 1 && set === "01234") return "평일마다" + tail;
    if (r.interval === 1 && set === "56") return "주말마다" + tail;
    const prefix = r.interval === 1 ? "매주" : r.interval === 2 ? "격주" : `${r.interval}주마다`;
    return (days.length ? `${prefix} ${dayList(days)}` : prefix) + tail;
  }
  const prefix = r.interval === 1 ? (r.freq === "MONTHLY" ? "매달" : "매년") : `${r.interval}${UNIT_NAMES[r.freq]}마다`;
  if (r.freq === "MONTHLY") {
    if (r.byDay?.length) return `${prefix} ${r.byDay.map((d) => `${NTH_NAMES[d.nth ?? 1]} ${WEEKDAY_NAMES[d.day]}요일`).join(", ")}` + tail;
    if (r.byMonthDay === -1) return `${prefix} 마지막 날` + tail;
    return (r.byMonthDay ? `${prefix} ${r.byMonthDay}일` : prefix) + tail;
  }
  if (r.byMonth && r.byMonthDay) return `${prefix} ${r.byMonth}월 ${r.byMonthDay === -1 ? "말일" : `${r.byMonthDay}일`}` + tail;
  return prefix + tail;
}
